import { Component, Renderer2, OnInit, OnDestroy, Inject } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { RelevantWhitepaperComponent } from '../relevant-whitepaper/relevant-whitepaper.component';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { ResourcesService } from '../../../services/resources.service';
import { HttpClientModule } from '@angular/common/http';

@Component({
  selector: 'app-structural-design-patterns',
  imports: [CommonModule, RelevantWhitepaperComponent, ReactiveFormsModule],
  standalone: true,
  providers: [ResourcesService],
  templateUrl: './structural-design-patterns.component.html',
  styleUrl: './structural-design-patterns.component.scss'
})
export class StructuralDesignPatternsComponent {
  isModalOpen: boolean = false;
  downloadForm: FormGroup;
  isSubmitting: boolean = false;
  submitSuccess: boolean = false;
  errorMessage: string = '';
  private jsonLdScript: any;

  // IMPORTANT: This string matches the Key added to the Dictionary in EmailService.cs
  readonly whitepaperTitle = "Structural Design Pattern";

  // Path for direct browser download (optional, if you want them to get it immediately + email)
  readonly pdfPath = "../../../../assets/resources/whitepaper/structuraldesignpattern/StructuralDesignPattern.pdf";

  constructor(
    private fb: FormBuilder,
    private resourcesService: ResourcesService,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private _document: Document
  ) {
    this.downloadForm = this.fb.group({
      name: ['', [Validators.required, Validators.pattern('^[A-Za-z]+( [A-Za-z]+)*$')]], // Added pattern for consistency with apply-position
      email: ['', [Validators.required, Validators.pattern('^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\\.[A-Za-z]{3,4}$')]]
    });
  }
  ngOnInit(): void {
    this.injectSchemaMarkup();
  }

  ngOnDestroy(): void {
    this.removeSchemaMarkup();
  }

  injectSchemaMarkup(): void {
    const structuralSchema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://www.kalpitatechnologies.com/white-paper/structural-design-patterns",
          "url": "https://www.kalpitatechnologies.com/white-paper/structural-design-patterns",
          "name": "Structural Design Pattern — Kalpita Technologies White Paper",
          "description": "A comprehensive white paper on structural design patterns including Adapter, Bridge, Composite, Decorator, Facade, Flyweight, and Proxy.",
          "isPartOf": {
            "@type": "WebSite",
            "@id": "https://www.kalpitatechnologies.com/#website",
            "name": "Kalpita Technologies",
            "url": "https://www.kalpitatechnologies.com/"
          }
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://www.kalpitatechnologies.com/white-paper/structural-design-patterns#breadcrumb",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "item": { "@type": "WebPage", "@id": "https://www.kalpitatechnologies.com/", "name": "Home" } },
            { "@type": "ListItem", "position": 2, "item": { "@type": "WebPage", "@id": "https://www.kalpitatechnologies.com/white-paper", "name": "White Papers" } },
            { "@type": "ListItem", "position": 3, "item": { "@type": "WebPage", "@id": "https://www.kalpitatechnologies.com/white-paper/structural-design-patterns", "name": "Structural Design Pattern" } }
          ]
        },
        {
          "@type": "TechArticle",
          "headline": "Structural Design Pattern",
          "articleBody": "This white paper delves into the fundamental structural design patterns that aid in creating robust, scalable, and maintainable software solutions...",
          "author": { "@type": "Organization", "name": "Kalpita Technologies" },
          "publisher": {
            "@type": "Organization",
            "name": "Kalpita Technologies",
            "logo": { "@type": "ImageObject", "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png" }
          }
        },
        {
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What are structural design patterns in software engineering?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Structural design patterns define efficient ways to compose classes and objects into larger structures while maintaining flexibility and reusability."
              }
            }
          ]
        },
        {
          "@type": "Organization",
          "@id": "https://www.kalpitatechnologies.com/#organization",
          "name": "Kalpita Technologies",
          "url": "https://www.kalpitatechnologies.com/",
          "logo": { "@type": "ImageObject", "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png" }
        }
      ]
    };

    this.jsonLdScript = this.renderer.createElement('script');
    this.jsonLdScript.type = 'application/ld+json';
    this.jsonLdScript.text = JSON.stringify(structuralSchema);
    this.renderer.appendChild(this._document.head, this.jsonLdScript);
  }

  removeSchemaMarkup(): void {
    if (this.jsonLdScript) {
      this.renderer.removeChild(this._document.head, this.jsonLdScript);
      this.jsonLdScript = null;
    }
  }
  openModal() {
    this.isModalOpen = true;
    this.submitSuccess = false;
    this.errorMessage = '';
    this.isSubmitting = false;
    this.downloadForm.reset();
  }

  closeModal() {
    this.isModalOpen = false;
  }

  onSubmit() {
    if (this.downloadForm.valid) {
      this.isSubmitting = true;
      this.errorMessage = '';

      // Construct Payload matching C# WhitepaperDownloadForm
      const payload = {
        Name: this.downloadForm.value.name,
        Email: this.downloadForm.value.email,
        WhitepaperTitle: this.whitepaperTitle
      };

      this.resourcesService.sendWhitepaperEmail(payload).subscribe({
        next: (response: any) => {
          // Check backend response structure. Usually returns { IsError: false, data: true }
          if (response && !response.IsError) {
            this.handleSuccess();
          } else {
            this.isSubmitting = false;
            this.errorMessage = response?.Message || 'Failed to send email. Please try again.';
          }
        },
        error: (error) => {
          this.isSubmitting = false;
          console.error('Error sending email:', error);
          this.errorMessage = 'Server error. Please check your internet connection.';
        }
      });
    } else {
      this.downloadForm.markAllAsTouched();
    }
  }

  handleSuccess() {
    this.isSubmitting = false;
    this.submitSuccess = true;

    // OPTIONAL: Trigger automatic browser download alongside email
    // this.triggerFileDownload(); 

    // Close modal after 4 seconds
    setTimeout(() => {
      this.closeModal();
    }, 4000);
  }

  triggerFileDownload() {
    const link = document.createElement('a');
    link.href = this.pdfPath;
    link.download = 'rise-of-ai-agent.pdf';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}
